import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError, funnelApi, initialSessionId, replaceSessionId } from './lib/api'
import { useFunnelStore } from './store/funnel'
import { ErrorPanel, Layout, PageSkeleton } from './components/Layout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { NodeScreen } from './screens/NodeScreen'
import { ExitScreen } from './screens/ExitScreen'
import { HandoffScreen } from './screens/HandoffScreen'

function FunnelApp() {
  const { i18n } = useTranslation()
  const hydrate = useFunnelStore((state) => state.hydrate)
  const setResearchConsent = useFunnelStore((state) => state.setResearchConsent)

  // The server session is the source of truth: a refresh mid-funnel restores
  // every answer, so nothing is kept in localStorage.
  const session = useQuery({
    queryKey: ['funnel-session', initialSessionId],
    retry: false,
    queryFn: async () => {
      try {
        return await funnelApi.get(initialSessionId)
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) throw error
        try {
          const created = await funnelApi.create(
            initialSessionId,
            i18n.language === 'el' ? 'el' : 'en'
          )
          return {
            session: {
              id: created.sessionId,
              status: 'started',
              locale: i18n.language === 'el' ? ('el' as const) : ('en' as const),
              termsVersion: null,
              privacyVersion: null,
              agreementAcceptedAt: null,
              researchConsent: null,
              claimedAt: null,
            },
            responses: {},
            experiments: created.experiments,
          }
        } catch (createError) {
          // Session id collision — mint a fresh one and start over.
          if (createError instanceof ApiError && createError.status === 409) {
            replaceSessionId()
            window.location.reload()
          }
          throw createError
        }
      }
    },
  })

  useEffect(() => {
    if (!session.data) return
    hydrate(session.data.responses)
    setResearchConsent(session.data.session.researchConsent)
  }, [hydrate, session.data, setResearchConsent])

  if (session.isPending)
    return (
      <Layout>
        <PageSkeleton />
      </Layout>
    )
  if (session.isError)
    return (
      <Layout>
        <ErrorPanel retry={() => void session.refetch()} />
      </Layout>
    )

  return (
    <Routes>
      {/* The landing question owns the bare URL; every other screen is /f/:nodeId. */}
      <Route path="/" element={<NodeScreen />} />
      <Route path="/f/:nodeId" element={<NodeScreen />} />
      <Route
        path="/not-eligible"
        element={
          <Layout>
            <ExitScreen />
          </Layout>
        }
      />
      {/* Universal-link target (see public/.well-known/apple-app-site-association). */}
      <Route
        path="/open"
        element={
          <Layout>
            <HandoffScreen open />
          </Layout>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <FunnelApp />
      </ErrorBoundary>
    </BrowserRouter>
  )
}
